import { LayerData } from "./layers/layerData.model";


export default class MotiWebSocket {
    private socket: WebSocket | null = null;
    private onMessageHandler: (e: MessageEvent) => void
    private onCloseHandler: (ev: CloseEvent) => void
    private url: string;
    private sessionId: string | null = null;
    constructor() {
        this.url = import.meta.env.VITE_API_CREATE_WS_URL;
        this.onMessageHandler = (_: MessageEvent) => { }
        this.onCloseHandler = (ev: CloseEvent) => {
            console.log(`WebSocket ${this.sessionId} closed: ${ev.code}`)
            this.closeSocket()
        }
    }

    private setOnMessage(newMessageHandler: (e: MessageEvent) => void) {
        this.onMessageHandler = newMessageHandler
    }

    private setOnClose(newOnClose: (ev: CloseEvent) => void) {
        this.onCloseHandler = newOnClose;
    }

    private closeSocket() {
        console.log("Socket has been closed");
        this.socket?.close()
        this.socket = null;
        this.sessionId = null;
    }

    private isSocketReady() {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            console.error("Socket not connected");
            return false;
        }
        return true;
    }

    async connect(): Promise<void> {
        if (this.socket) this.socket.close()
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(this.url);
            ws.binaryType = 'blob'
            ws.onmessage = (e) => this.onMessageHandler(e)
            ws.onclose = (ev) => { this.onCloseHandler(ev) }
            ws.onopen = () => resolve()
            ws.onerror = (e) => reject(e)
            this.sessionId = crypto.randomUUID();
            this.socket = ws;
            console.log("Creating a new socket session:", this.sessionId)
        })
    }


    async parse(data: any): Promise<string | null> {
        if (!this.isSocketReady()) {
            return null
        }
        return new Promise((resolve, reject) => {

            this.setOnMessage((e: MessageEvent) => {
                let msg: any
                try {
                    msg = JSON.parse(e.data);
                } catch {
                    console.error("Invalid JSON from server:", e.data);
                    reject(null);
                }

                if (msg.frame) {
                    resolve(msg.frame);
                }

                if (msg.error) {
                    console.error(msg.error);
                    reject(null);
                }
            });
            try {
                this.socket!.send(JSON.stringify({
                    type: 'parse',
                    id: this.sessionId,
                    verbose: true,
                    payload: { layer: data }
                }));

            } catch (e) {
                console.error(e)
                reject(null);
            }
        })
    }

    async getFrame(time: number): Promise<string | null> {
        if (!this.isSocketReady()) {
            return null
        }
        return new Promise((resolve, reject) => {

            this.setOnMessage((e: MessageEvent) => {
                let msg: any
                try {
                    msg = JSON.parse(e.data);
                } catch {
                    console.error("Invalid JSON from server:", e.data);
                    reject(null);
                }

                if (msg.frame) {
                    resolve(msg.frame);
                }

                if (msg.error) {
                    console.error(msg.error);
                    reject(null);
                }
            });
            try {
                this.socket!.send(JSON.stringify({
                    type: 'get_frame',
                    id: this.sessionId,
                    verbose: false,
                    payload: { time: time }
                }));

            } catch (e) {
                console.error(e)
                reject(null);
            }
        })
    }

    async render(layer: LayerData, mode: "file" | "s3_key", onUpdateRenderProgress: (percentage: number) => void): Promise<File | { bucket: string, s3_key: string, metadata: { width: number, height: number } } | null> {
        if (!this.isSocketReady()) {
            return null
        }
        return new Promise((resolve, reject) => {
            this.setOnClose((ev: CloseEvent) => {
                console.log(`WebSocket ${this.sessionId} closed: ${ev.code}`)
                this.closeSocket()
                reject(new Error("WebSocket closed early"));
            })
            this.setOnMessage((e: MessageEvent) => {
                let msg: any
                try {
                    msg = JSON.parse(e.data);
                } catch {
                    console.error("Invalid JSON from server:", e.data);
                    reject(null);
                }

                if (msg.file) {
                    const byteArray = Uint8Array.from(atob(msg.file), c => c.charCodeAt(0));
                    const blob = new Blob([byteArray], { type: 'video/mp4' });
                    const mediaFile = new File([byteArray], 'media.mp4', { type: blob.type });

                    resolve(mediaFile);
                    this.closeSocket();
                }

                if (msg.render_progress) {
                    onUpdateRenderProgress(msg.render_progress);
                }

                if (msg.s3_key) {
                    resolve(msg);
                    this.closeSocket();
                }

                if (msg.error) {
                    console.error(msg.error);
                    reject(new Error(msg.error));
                    this.closeSocket();
                }
            });
            try {
                this.socket!.send(JSON.stringify({
                    type: 'render_new',
                    id: this.sessionId,
                    verbose: false,
                    payload: { layer: layer, return_type: mode }
                }));

            } catch (e) {
                console.error(e)
                reject(null);
                this.closeSocket();
            }
        })
    }


    async convert(
        url: string,
        onUpdateConvertPercentage: (percentage: number) => void
    ): Promise<File | null> {
        if (!this.isSocketReady()) {
            return null;
        }

        const chunks: Blob[] = [];

        return new Promise((resolve, reject) => {
            this.setOnClose((ev: CloseEvent) => {
                console.log(`WebSocket ${this.sessionId} closed: ${ev.code}`)
                this.closeSocket()
                reject(new Error("WebSocket closed early"));
            })
            this.setOnMessage((e: MessageEvent) => {
                if (e.data instanceof Blob) {
                    chunks.push(e.data);
                    return;
                }

                let msg: any;
                try {
                    msg = JSON.parse(e.data);
                } catch {
                    console.error("Invalid JSON from server:", e.data);
                    return;
                }

                if (msg.convert_progress != null) {
                    onUpdateConvertPercentage(msg.convert_progress);
                }

                if (msg.upload_complete) {
                    const blob = new Blob(chunks, { type: "video/webm" });
                    const resultFile = new File([blob], "video.webm", {
                        type: blob.type,
                        lastModified: Date.now()
                    });
                    this.closeSocket();
                    resolve(resultFile);
                }

                if (msg.error) {
                    this.closeSocket();
                    reject(new Error(msg.error));
                }
            });

            this.socket!.send(
                JSON.stringify({
                    type: "convert_url",
                    payload: { url }
                })
            );
        });
    }
}